import { ArrowRight, Phone, Mail, Calendar, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/animations/ScrollAnimations";

export function ProductCTANew() {
  return (
    <section className="py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5" />
      
      {/* Ambient orbs */}
      <motion.div 
        className="absolute top-1/4 right-1/4 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ 
          background: 'radial-gradient(circle, hsl(var(--primary) / 0.12) 0%, transparent 60%)',
          filter: 'blur(80px)'
        }}
        animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.6, 0.4] }}
        transition={{ duration: 10, repeat: Infinity }}
      />
      
      <div className="absolute top-0 left-0 w-full section-divider" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <ScrollReveal>
            <motion.div 
              className="inline-flex items-center gap-2 badge-primary mb-8"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Get Started
            </motion.div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8 tracking-tight">
              Ready to <span className="text-gradient">Transform</span> Your Network?
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-12 font-light leading-relaxed">
              Start your free trial today or schedule a personalized demo to see how our platform can power your EV charging business.
            </p>
          </ScrollReveal>
          
          <motion.div 
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
              <Button variant="hero" size="xl" className="group shadow-lg shadow-primary/20 text-base px-10 py-7" asChild>
                <Link to="/contact">
                  Start Free Trial
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
              <a href="https://outlook.office365.com/book/EvGatewayPortalDemos@evgateway.com/" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="xl" className="border-foreground/30 text-foreground hover:bg-foreground/10 hover:border-foreground/50 text-base px-10 py-7">
                  Schedule Demo
                </Button>
              </a>
            </motion.div>
          </motion.div>

          {/* Contact Options */}
          <StaggerContainer className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto" staggerDelay={0.1}>
            {[
              { icon: Calendar, title: "Book Demo", action: "Schedule Now", color: "primary", link: "https://outlook.office365.com/book/EvGatewayPortalDemos@evgateway.com/" },
              { icon: Phone, title: "Sales", action: "Contact Us", color: "accent", link: "/contact", isInternal: true },
              { icon: Mail, title: "Email", action: "sales@evgateway.com", color: "primary" },
            ].map((option, idx) => (
              <StaggerItem key={idx}>
                {option.link ? (
                  option.isInternal ? (
                    <Link to={option.link}>
                      <motion.div 
                        whileHover={{ scale: 1.03, y: -4 }}
                        className="glass-card p-6 hover:border-primary/40 transition-all cursor-pointer text-center"
                      >
                        <div className={`w-12 h-12 rounded-xl bg-${option.color}/10 border border-${option.color}/20 flex items-center justify-center mx-auto mb-4`}>
                          <option.icon className={`h-6 w-6 text-${option.color}`} />
                        </div>
                        <div className="font-semibold mb-1 text-white">{option.title}</div>
                        <div className={`text-sm text-${option.color}`}>{option.action}</div>
                      </motion.div>
                    </Link>
                  ) : (
                    <a href={option.link} target="_blank" rel="noopener noreferrer">
                      <motion.div 
                        whileHover={{ scale: 1.03, y: -4 }}
                        className="glass-card p-6 hover:border-primary/40 transition-all cursor-pointer text-center"
                      >
                        <div className={`w-12 h-12 rounded-xl bg-${option.color}/10 border border-${option.color}/20 flex items-center justify-center mx-auto mb-4`}>
                          <option.icon className={`h-6 w-6 text-${option.color}`} />
                        </div>
                        <div className="font-semibold mb-1 text-white">{option.title}</div>
                        <div className={`text-sm text-${option.color}`}>{option.action}</div>
                      </motion.div>
                    </a>
                  )
                ) : (
                  <motion.div 
                    whileHover={{ scale: 1.03, y: -4 }}
                    className="glass-card p-6 text-center"
                  >
                    <div className={`w-12 h-12 rounded-xl bg-${option.color}/10 border border-${option.color}/20 flex items-center justify-center mx-auto mb-4`}>
                      <option.icon className={`h-6 w-6 text-${option.color}`} />
                    </div>
                    <div className="font-semibold mb-1 text-white">{option.title}</div>
                    <div className={`text-sm text-${option.color}`}>{option.action}</div>
                  </motion.div>
                )}
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </div>
    </section>
  );
}